import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaDownload, FaSyncAlt } from 'react-icons/fa'
import api from '../../../../helpers/apiRequester'
import shared from '../shared/mhlShared.module.scss'
import styles from './MilitaryHearingLossAdminStats.module.scss'
import { Pill, StatTile } from '../shared/SourceTabs'
import type { AgentStatRow, AgentStatTotals, TeamStatRow } from '../shared/mhlTypes'
import { CollapsibleSection } from '../../omnibusShared/collapsibleSection'
import { TablePagination, usePagedRows } from '../shared/TablePagination'
import { colors } from '../../../../ui'

const EMPTY_TOTALS: AgentStatTotals = {
    leadsSubmittedToday: 0,
    leadsSubmitted7d: 0,
    leadsSubmitted30d: 0,
    leadsSubmittedTotal: 0,
    aboveQuantum: 0,
    belowQuantum: 0,
    tbcQuantum: 0,
    awaitingReview: 0,
    approved: 0,
    rejected: 0,
    linksSent: 0,
    signed: 0,
    conversion: 0,
}

type SortKey = keyof Omit<AgentStatRow, 'agentKey' | 'cognitoId' | 'organisation' | 'role' | 'status' | 'teamId'>

const PRESETS: { label: string; days: number | null }[] = [
    { label: 'All time', days: null },
    { label: '7 days', days: 7 },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
]

const isoDate = (d: Date) => d.toISOString().slice(0, 10)

const csvCell = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

const ROLE_LABEL: Record<AgentStatRow['role'], string> = { admin: 'MHL Admin', team_leader: 'Team leader', agent: 'Agent', other: 'Other' }

export const MilitaryHearingLossAdminStats = () => {
    const navigate = useNavigate()
    const [agents, setAgents] = useState<AgentStatRow[]>([])
    const [totals, setTotals] = useState<AgentStatTotals>(EMPTY_TOTALS)
    const [teamRows, setTeamRows] = useState<TeamStatRow[]>([])
    const [teamFilter, setTeamFilter] = useState<'all' | 'unassigned' | number>('all')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [forbidden, setForbidden] = useState(false)
    // Server-declared visibility; team leaders see only their team.
    const [scope, setScope] = useState<{ all: boolean; teamName: string | null; isTeamLeader: boolean } | null>(null)
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [hideInactive, setHideInactive] = useState(false)
    const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'leadsSubmittedTotal', dir: 'desc' })

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
            const res = await api({ url: '/military-hearing-loss-stats', data: { timezone, from: from || undefined, to: to || undefined } })
            if (!res.data?.success) throw new Error(res.data?.message || 'Failed to load stats')
            setAgents(res.data.agents || res.data.ats || [])
            setTotals({ ...EMPTY_TOTALS, ...(res.data.totals || {}) })
            setTeamRows(res.data.teams || [])
            setScope(res.data.scope ?? null)
            setForbidden(false)
        } catch (err: any) {
            if (err?.response?.status === 403) {
                setForbidden(true)
                setAgents([])
                setTeamRows([])
                setTotals(EMPTY_TOTALS)
                return
            }
            setError(err?.response?.data?.message || err?.message || 'Failed to load stats')
        } finally {
            setLoading(false)
        }
    }, [from, to])

    useEffect(() => {
        void load()
    }, [load])

    const applyPreset = (days: number | null) => {
        if (days === null) {
            setFrom('')
            setTo('')
            return
        }
        const end = new Date()
        const start = new Date()
        start.setDate(end.getDate() - (days - 1))
        setFrom(isoDate(start))
        setTo(isoDate(end))
    }

    const activePreset = useMemo(() => {
        if (!from && !to) return null
        const end = new Date()
        for (const p of PRESETS) {
            if (p.days === null) continue
            const start = new Date()
            start.setDate(end.getDate() - (p.days - 1))
            if (from === isoDate(start) && to === isoDate(end)) return p.days
        }
        return undefined
    }, [from, to])

    const rows = useMemo(() => {
        let list = hideInactive ? agents.filter((a) => a.leadsSubmittedTotal > 0) : agents
        if (teamFilter !== 'all') list = list.filter((a) => (teamFilter === 'unassigned' ? a.teamId == null : a.teamId === teamFilter))
        const dir = sort.dir === 'asc' ? 1 : -1
        return [...list].sort((a, b) => {
            const av = a[sort.key]
            const bv = b[sort.key]
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir || a.agent.localeCompare(b.agent)
            return String(av ?? '').localeCompare(String(bv ?? '')) * dir
        })
    }, [agents, hideInactive, sort, teamFilter])

    const {
        rows: pagedRows,
        page: statsPage,
        totalPages: statsTotalPages,
        setPage: setStatsPage,
        pageSize: statsPageSize,
    } = usePagedRows(rows)

    const toggleSort = (key: SortKey) =>
        setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'agent' || key === 'email' ? 'asc' : 'desc' }))

    const sortMark = (key: SortKey) => (sort.key === key ? <span className={styles.sortMark}>{sort.dir === 'asc' ? '▲' : '▼'}</span> : null)

    const exportCsv = () => {
        const header = [
            'Agent',
            'Email',
            'Role',
            'Team',
            'Status',
            'Today',
            'Last 7 days',
            'Last 30 days',
            'Total',
            'Above quantum',
            'Below quantum',
            'Quantum TBC',
            'Awaiting review',
            'Approved',
            'Rejected',
            'E-sign links sent',
            'Signed',
            'Conversion %',
        ]
        const lines = rows.map((a) =>
            [
                a.agent,
                a.email,
                ROLE_LABEL[a.role],
                a.teamName || '',
                a.status,
                a.leadsSubmittedToday,
                a.leadsSubmitted7d,
                a.leadsSubmitted30d,
                a.leadsSubmittedTotal,
                a.aboveQuantum,
                a.belowQuantum,
                a.tbcQuantum,
                a.awaitingReview,
                a.approved,
                a.rejected,
                a.linksSent,
                a.signed,
                a.conversion,
            ]
                .map(csvCell)
                .join(','),
        )
        const totalLine = [
            'TOTAL',
            '',
            '',
            '',
            '',
            totals.leadsSubmittedToday,
            totals.leadsSubmitted7d,
            totals.leadsSubmitted30d,
            totals.leadsSubmittedTotal,
            totals.aboveQuantum,
            totals.belowQuantum,
            totals.tbcQuantum,
            totals.awaitingReview,
            totals.approved,
            totals.rejected,
            totals.linksSent,
            totals.signed,
            totals.conversion,
        ]
            .map(csvCell)
            .join(',')
        const csv = [header.join(','), ...lines, totalLine].join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        const rangeTag = from || to ? `_${from || 'start'}_to_${to || 'today'}` : '_all-time'
        a.href = url
        a.download = `mhl-agent-stats${rangeTag}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    const rangeLabel = from || to ? `${from || 'start'} → ${to || 'today'}` : 'all time'

    const numericHeader = (key: SortKey, label: string) => (
        <th className={`${shared.num} ${styles.sortable}`} onClick={() => toggleSort(key)}>
            {label}
            {sortMark(key)}
        </th>
    )

    if (forbidden) {
        return (
            <div className={shared.wrap}>
                <div className={shared.emptyState}>
                    <p style={{ margin: 0, fontWeight: 600, color: colors.ink }}>Onboarding stats aren’t available to you</p>
                    <p className={shared.hint} style={{ marginTop: 6 }}>
                        Stats are visible to team leaders (for their team) and MHL admins. Ask your team leader or an admin if you need these figures.
                    </p>
                </div>
            </div>
        )
    }

    const scoped = !!scope && !scope.all

    return (
        <div className={shared.wrap}>
            <header className={shared.pageHeader}>
                <p className={shared.hint}>
                    {scoped ? (
                        scope?.teamName ? (
                            <>
                                Volume supplied by your team, <strong>{scope.teamName}</strong>, through the dashboard.
                            </>
                        ) : (
                            <>Volume you have supplied through the dashboard.</>
                        )
                    ) : (
                        <>
                            Volume supplied by each Military Hearing Loss agent through the dashboard. Attribution uses the agent's Cognito identity, so it survives name and
                            email changes.
                        </>
                    )}{' '}
                    Webform leads are excluded. Showing <strong>{rangeLabel}</strong>.
                </p>
                <div className={shared.headerActions}>
                    <button type="button" className={shared.btn} onClick={() => void load()} disabled={loading}>
                        <FaSyncAlt size={11} /> Refresh
                    </button>
                    <button type="button" className={`${shared.btn} ${shared.btnPrimary}`} onClick={exportCsv} disabled={loading || !rows.length}>
                        <FaDownload size={11} /> Export CSV
                    </button>
                </div>
            </header>

            <div className={shared.toolbar}>
                <div className={styles.presets}>
                    {PRESETS.map((p) => (
                        <button
                            key={p.label}
                            type="button"
                            className={`${shared.btn} ${shared.btnSmall} ${activePreset === p.days ? styles.presetActive : ''}`}
                            onClick={() => applyPreset(p.days)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                <label className={styles.dateField}>
                    <span>From</span>
                    <input type="date" className={shared.dateInput} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
                </label>
                <label className={styles.dateField}>
                    <span>To</span>
                    <input type="date" className={shared.dateInput} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
                </label>
                {!scoped && teamRows.length > 0 && (
                    <select
                        className={shared.select}
                        value={teamFilter === 'all' || teamFilter === 'unassigned' ? teamFilter : String(teamFilter)}
                        onChange={(e) => setTeamFilter(e.target.value === 'all' || e.target.value === 'unassigned' ? e.target.value : Number(e.target.value))}
                    >
                        <option value="all">All teams</option>
                        {teamRows.map((t) => (
                            <option key={t.teamId ?? 'unassigned'} value={t.teamId ?? 'unassigned'}>
                                {t.teamName}
                            </option>
                        ))}
                    </select>
                )}
                <label className={shared.checkboxRow}>
                    <input type="checkbox" checked={hideInactive} onChange={(e) => setHideInactive(e.target.checked)} />
                    Hide agents with no submissions
                </label>
                <span className={shared.resultCount}>{rows.length} agents</span>
            </div>

            {error && <div className={shared.errorBanner}>{error}</div>}

            <div className={shared.statGrid}>
                <StatTile label="Submitted (range)" value={totals.leadsSubmittedTotal} accent />
                <StatTile label="Today" value={totals.leadsSubmittedToday} />
                <StatTile label="Last 7 days" value={totals.leadsSubmitted7d} />
                <StatTile label="Last 30 days" value={totals.leadsSubmitted30d} />
                <StatTile label="Above quantum" value={totals.aboveQuantum} tone="good" note="≥ 4 dB NIHL" />
                <StatTile label="Below quantum" value={totals.belowQuantum} tone={totals.belowQuantum ? 'bad' : undefined} note="< 4 dB NIHL" />
                <StatTile label="Quantum TBC" value={totals.tbcQuantum} tone={totals.tbcQuantum ? 'warn' : undefined} />
                <StatTile
                    label="Awaiting review"
                    value={totals.awaitingReview}
                    tone={totals.awaitingReview ? 'warn' : undefined}
                    onClick={() => navigate('/military-hearing-loss/leads?review=awaiting_review')}
                />
                <StatTile label="Approved" value={totals.approved} tone="good" />
                <StatTile label="Rejected" value={totals.rejected} tone={totals.rejected ? 'bad' : undefined} />
                <StatTile label="Signed" value={totals.signed} tone="good" note={`${totals.linksSent} links sent`} />
                <StatTile label="Conversion" value={`${totals.conversion}%`} note="signed ÷ approved" />
            </div>

            {!scoped && teamRows.length > 0 && (teamRows.length > 1 || teamRows[0].teamId !== null) && (
                <CollapsibleSection title="Volume by team" count={teamRows.filter((t) => t.teamId !== null).length} hint="Teams group agents for reporting only." defaultOpen>
                    <div className={shared.tableWrap} style={{ borderRadius: 8 }}>
                        <table className={`${shared.table} ${styles.statsTable}`}>
                            <thead>
                                <tr>
                                    <th>Team</th>
                                    <th className={shared.num}>Agents</th>
                                    <th className={shared.num}>Active</th>
                                    <th className={shared.num}>Today</th>
                                    <th className={shared.num}>7d</th>
                                    <th className={shared.num}>30d</th>
                                    <th className={shared.num}>Total</th>
                                    <th className={shared.num}>Above</th>
                                    <th className={shared.num}>Below</th>
                                    <th className={shared.num}>TBC</th>
                                    <th className={shared.num}>Awaiting</th>
                                    <th className={shared.num}>Approved</th>
                                    <th className={shared.num}>Signed</th>
                                    <th className={shared.num}>Conv.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamRows.map((t) => (
                                    <tr
                                        key={t.teamId ?? 'unassigned'}
                                        className={`${shared.rowClickable} ${t.teamId === null ? shared.rowMuted : ''}`}
                                        onClick={() => setTeamFilter(teamFilter === (t.teamId ?? 'unassigned') ? 'all' : t.teamId ?? 'unassigned')}
                                        title="Filter the agent table to this team"
                                    >
                                        <td>
                                            <span className={shared.stackPrimary}>{t.teamName}</span>
                                        </td>
                                        <td className={shared.num}>{t.agents}</td>
                                        <td className={shared.num}>{t.activeAgents}</td>
                                        <td className={shared.num}>{t.leadsSubmittedToday}</td>
                                        <td className={shared.num}>{t.leadsSubmitted7d}</td>
                                        <td className={shared.num}>{t.leadsSubmitted30d}</td>
                                        <td className={shared.num}>
                                            <strong>{t.leadsSubmittedTotal}</strong>
                                        </td>
                                        <td className={`${shared.num} ${styles.good}`}>{t.aboveQuantum}</td>
                                        <td className={`${shared.num} ${styles.bad}`}>{t.belowQuantum}</td>
                                        <td className={`${shared.num} ${styles.warn}`}>{t.tbcQuantum}</td>
                                        <td className={shared.num}>{t.awaitingReview}</td>
                                        <td className={shared.num}>{t.approved}</td>
                                        <td className={shared.num}>{t.signed}</td>
                                        <td className={shared.num}>{t.conversion}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CollapsibleSection>
            )}

            <div className={shared.tableWrap}>
                <table className={`${shared.table} ${styles.statsTable}`}>
                    <thead>
                        <tr>
                            <th className={styles.sortable} onClick={() => toggleSort('agent')}>
                                Agent{sortMark('agent')}
                            </th>
                            <th>Role</th>
                            <th className={styles.sortable} onClick={() => toggleSort('teamName')}>
                                Team{sortMark('teamName')}
                            </th>
                            {numericHeader('leadsSubmittedToday', 'Today')}
                            {numericHeader('leadsSubmitted7d', '7d')}
                            {numericHeader('leadsSubmitted30d', '30d')}
                            {numericHeader('leadsSubmittedTotal', 'Total')}
                            {numericHeader('aboveQuantum', 'Above')}
                            {numericHeader('belowQuantum', 'Below')}
                            {numericHeader('tbcQuantum', 'TBC')}
                            {numericHeader('awaitingReview', 'Awaiting')}
                            {numericHeader('approved', 'Approved')}
                            {numericHeader('rejected', 'Rejected')}
                            {numericHeader('linksSent', 'Links')}
                            {numericHeader('signed', 'Signed')}
                            {numericHeader('conversion', 'Conv.')}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={16} className={styles.emptyCell}>
                                    Loading…
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={16} className={styles.emptyCell}>
                                    No agent activity {from || to ? 'in this range' : 'yet'}.
                                </td>
                            </tr>
                        ) : (
                            pagedRows.map((a) => (
                                <tr key={a.agentKey} className={a.leadsSubmittedTotal === 0 ? shared.rowMuted : undefined}>
                                    <td>
                                        <div className={shared.stackCell}>
                                            <span className={shared.stackPrimary}>{a.agent}</span>
                                            <span className={shared.stackSecondary}>{a.email}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.roleCell}>
                                            <Pill tone={a.role === 'admin' ? 'brand' : a.role === 'team_leader' ? 'info' : 'muted'}>{ROLE_LABEL[a.role]}</Pill>
                                            {a.status && a.status !== 'active' && <Pill tone="bad">{a.status}</Pill>}
                                        </div>
                                    </td>
                                    <td className={a.teamName ? undefined : shared.stackSecondary}>{a.teamName || '—'}</td>
                                    <td className={shared.num}>{a.leadsSubmittedToday}</td>
                                    <td className={shared.num}>{a.leadsSubmitted7d}</td>
                                    <td className={shared.num}>{a.leadsSubmitted30d}</td>
                                    <td className={shared.num}>
                                        <strong>{a.leadsSubmittedTotal}</strong>
                                    </td>
                                    <td className={`${shared.num} ${styles.good}`}>{a.aboveQuantum}</td>
                                    <td className={`${shared.num} ${styles.bad}`}>{a.belowQuantum}</td>
                                    <td className={`${shared.num} ${styles.warn}`}>{a.tbcQuantum}</td>
                                    <td className={shared.num}>{a.awaitingReview}</td>
                                    <td className={shared.num}>{a.approved}</td>
                                    <td className={shared.num}>{a.rejected}</td>
                                    <td className={shared.num}>{a.linksSent}</td>
                                    <td className={shared.num}>{a.signed}</td>
                                    <td className={shared.num}>{a.conversion}%</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {rows.length > 0 && (
                        <tfoot>
                            <tr className={styles.totalsRow}>
                                <td>Total</td>
                                <td />
                                <td />
                                <td className={shared.num}>{totals.leadsSubmittedToday}</td>
                                <td className={shared.num}>{totals.leadsSubmitted7d}</td>
                                <td className={shared.num}>{totals.leadsSubmitted30d}</td>
                                <td className={shared.num}>{totals.leadsSubmittedTotal}</td>
                                <td className={shared.num}>{totals.aboveQuantum}</td>
                                <td className={shared.num}>{totals.belowQuantum}</td>
                                <td className={shared.num}>{totals.tbcQuantum}</td>
                                <td className={shared.num}>{totals.awaitingReview}</td>
                                <td className={shared.num}>{totals.approved}</td>
                                <td className={shared.num}>{totals.rejected}</td>
                                <td className={shared.num}>{totals.linksSent}</td>
                                <td className={shared.num}>{totals.signed}</td>
                                <td className={shared.num}>{totals.conversion}%</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
                <TablePagination
                    page={statsPage}
                    totalPages={statsTotalPages}
                    total={rows.length}
                    pageSize={statsPageSize}
                    onChange={setStatsPage}
                />
            </div>
        </div>
    )
}
